/*
 * Copyright Elasticsearch B.V. and/or licensed to Elasticsearch B.V. under one
 * or more contributor license agreements. Licensed under the Elastic License
 * 2.0 and the Server Side Public License, v 1; you may not use this file except
 * in compliance with, at your election, the Elastic License 2.0 or the Server
 * Side Public License, v 1.
 */
import React from 'react';
import PropTypes from "prop-types";
import classNames from 'classnames';
import { keysOf } from '../../common';
import { EuiPageHeaderContent } from './page_header_content';
import { setPropsForRestrictedPageWidth } from '../_restrict_width';
var paddingSizeToClassNameMap = {
  none: null,
  s: 'euiPageHeader--paddingSmall',
  m: 'euiPageHeader--paddingMedium',
  l: 'euiPageHeader--paddingLarge'
};
export var PADDING_SIZES = keysOf(paddingSizeToClassNameMap);
export var EuiPageHeader = ({
  className,
  restrictWidth = false,
  paddingSize = 'none',
  bottomBorder,
  style,
  // Page header content shared props:
  alignItems,
  responsive = true,
  children,
  // Page header content only props:
  pageTitle,
  pageTitleProps,
  iconType,
  iconProps,
  tabs,
  tabsProps,
  breadcrumbs,
  breadcrumbProps,
  description,
  rightSideItems,
  rightSideGroupProps,
  ...rest
}) => {
  const {
    widthClassName,
    newStyle
  } = setPropsForRestrictedPageWidth(restrictWidth, style);
  const classes = classNames('euiPageHeader', paddingSizeToClassNameMap[paddingSize], {
    [`euiPageHeader--${widthClassName}`]: widthClassName,
    'euiPageHeader--bottomBorder': bottomBorder,
    'euiPageHeader--responsive': responsive === true,
    'euiPageHeader--responsiveReverse': responsive === 'reverse',
    'euiPageHeader--tabsAtBottom': pageTitle && tabs,
    'euiPageHeader--onlyTabs': tabs && !pageTitle && !rightSideItems && !description && !children
  }, `euiPageHeader--${alignItems ?? 'center'}`, className);

  if (!pageTitle && !tabs && !description && !rightSideItems) {
    return <header className={classes} style={newStyle || style} {...rest}>
        {children}
      </header>;
  }

  return <header className={classes} style={newStyle || style} {...rest}>
      <EuiPageHeaderContent alignItems={alignItems} responsive={responsive} pageTitle={pageTitle} pageTitleProps={pageTitleProps} iconType={iconType} iconProps={iconProps} tabs={tabs} tabsProps={tabsProps} description={description} rightSideItems={rightSideItems} rightSideGroupProps={rightSideGroupProps} breadcrumbs={breadcrumbs} breadcrumbProps={breadcrumbProps}>
        {children}
      </EuiPageHeaderContent>
    </header>;
};
EuiPageHeader.propTypes = {
  /**
     * Adjust the padding.
     * When using this setting it's best to be consistent throughout all similar usages
     */
  paddingSize: PropTypes.any,

  /**
     * Adds a bottom border to separate it from the content after
     */
  bottomBorder: PropTypes.bool,
  className: PropTypes.string,
  "aria-label": PropTypes.string,
  "data-test-subj": PropTypes.string,

  /**
     * Set to false if you don't want the children to stack at small screen sizes.
     * Set to `reverse` to display the right side content first for the sake of hierarchy (like global time)
     */
  responsive: PropTypes.oneOfType([PropTypes.bool.isRequired, PropTypes.oneOf(["reverse"])]),

  /**
     * Vertical alignment of the left and right side content;
     * Default is `middle` for custom content, but `top` for when `pageTitle` or `tabs` are included
     */
  alignItems: PropTypes.any,

  /**
     * Pass custom an array of content to this side usually up to 3 buttons.
     * The first button should be primary, usually with `fill` and will be visually displayed as the last item,
     * but first in the tab order
     */
  rightSideItems: PropTypes.arrayOf(PropTypes.node.isRequired),

  /**
     * Additional EuiFlexGroup props to pass to the container of the `rightSideItems`
     */
  rightSideGroupProps: PropTypes.any,

  /**
     * Custom children will be rendered before the `tabs` unless no `pageTitle` is present, then it will be the last item
     */
  children: PropTypes.node,

  /**
     * Position is dependent on existing with a `pageTitle` or `tabs`
     * Automatically get wrapped in a single paragraph tag inside an EuiText block
     */
  description: PropTypes.oneOfType([PropTypes.string.isRequired, PropTypes.node.isRequired]),

  /**
     * Wrapped in an `H1` so choose appropriately.
     * A simple string is best
     */
  pageTitle: PropTypes.node,

  /**
     * Additional props to pass to the EuiTitle
     */
  pageTitleProps: PropTypes.shape({
    className: PropTypes.string,
    "aria-label": PropTypes.string,
    "data-test-subj": PropTypes.string,
    textTransform: PropTypes.any,
    id: PropTypes.string
  }),

  /**
     * Optional icon to place to the left of the title
     */
  iconType: PropTypes.oneOfType([PropTypes.oneOf(["accessibility", "addDataApp", "advancedSettingsApp", "agentApp", "aggregate", "alert", "analyzeEvent", "annotation", "apmApp", "apmTrace", "appSearchApp", "apps", "arrowDown", "arrowLeft", "arrowRight", "arrowUp", "arrowStart", "arrowEnd", "asterisk", "auditbeatApp", "beaker", "bell", "bellSlash", "bolt", "boxesHorizontal", "boxesVertical", "branch", "branchUser", "broom", "brush", "bug", "bullseye", "calendar", "canvasApp", "casesApp", "check", "checkInCircleFilled", "cheer", "classificationJob", "clock", "cloudDrizzle", "cloudStormy", "cloudSunny", "codeApp", "color", "compute", "console", "consoleApp", "continuityAbove", "continuityAboveBelow", "continuityBelow", "continuityWithin", "controlsHorizontal", "controlsVertical", "copy", "copyClipboard", "createAdvancedJob", "createMultiMetricJob", "createPopulationJob", "createSingleMetricJob", "cross", "crossClusterReplicationApp", "crossInACircleFilled", "crosshairs", "currency", "cut", "dashboardApp", "dataVisualizer", "database", "desktop", "devToolsApp", "discoverApp", "document", "documentEdit", "documentation", "documents", "dot", "doubleArrowLeft", "doubleArrowRight", "download", "editorAlignCenter", "editorAlignLeft", "editorAlignRight", "editorBold", "editorChecklist", "editorCodeBlock", "editorComment", "editorDistributeHorizontal", "editorDistributeVertical", "editorHeading", "editorItalic", "editorItemAlignBottom", "editorItemAlignCenter", "editorItemAlignLeft", "editorItemAlignMiddle", "editorItemAlignRight", "editorItemAlignTop", "editorLink", "editorOrderedList", "editorPositionBottomLeft", "editorPositionBottomRight", "editorPositionTopLeft", "editorPositionTopRight", "editorRedo", "editorStrike", "editorTable", "editorUnderline", "editorUndo", "editorUnorderedList", "email", "empty", "emsApp", "eql", "eraser", "exit", "expand", "expandMini", "exportAction", "eye", "eyeClosed", "faceHappy", "faceNeutral", "faceSad", "filebeatApp", "filter", "flag", "fleetApp", "fold", "folderCheck", "folderClosed", "folderExclamation", "folderOpen", "frameNext", "framePrevious", "fullScreen", "fullScreenExit", "function", "gear", "gisApp", "glasses", "globe", "grab", "grabHorizontal", "graphApp", "grid", "grokApp", "heart", "heartbeatApp", "heatmap", "help", "home", "iInCircle", "image", "importAction", "indexClose", "indexEdit", "indexFlush", "indexManagementApp", "indexMapping", "indexOpen", "indexPatternApp", "indexRollupApp", "indexRuntime", "indexSettings", "inputOutput", "inspect", "invert", "ip", "keyboardShortcut", "kqlField", "kqlFunction", "kqlOperand", "kqlSelector", "kqlValue", "layers", "lensApp", "lettering", "lineDashed", "lineDotted", "lineSolid", "link", "list", "listAdd", "lock", "lockOpen", "logoAWS", "logoAWSMono", "logoAerospike", "logoApache", "logoAppSearch", "logoAzure", "logoAzureMono", "logoBeats", "logoBusinessAnalytics", "logoCeph", "logoCloud", "logoCloudEnterprise", "logoCode", "logoCodesandbox", "logoCouchbase", "logoDocker", "logoDropwizard", "logoElastic", "logoElasticStack", "logoElasticsearch", "logoEnterpriseSearch", "logoEtcd", "logoGCP", "logoGCPMono", "logoGithub", "logoGmail", "logoGolang", "logoGoogleG", "logoHAproxy", "logoIBM", "logoIBMMono", "logoKafka", "logoKibana", "logoKubernetes", "logoLogging", "logoLogstash", "logoMaps", "logoMemcached", "logoMetrics", "logoMongodb", "logoMySQL", "logoNginx", "logoObservability", "logoOsquery", "logoPhp", "logoPostgres", "logoPrometheus", "logoRabbitmq", "logoRedis", "logoSecurity", "logoSiteSearch", "logoSketch", "logoSlack", "logoUptime", "logoWebhook", "logoWindows", "logoWorkplaceSearch", "logsApp", "logstashFilter", "logstashIf", "logstashInput", "logstashOutput", "logstashQueue", "machineLearningApp", "magnet", "magnifyWithExclamation", "magnifyWithMinus", "magnifyWithPlus", "managementApp", "mapMarker", "memory", "menu", "menuDown", "menuLeft", "menuRight", "menuUp", "merge", "metricbeatApp", "metricsApp", "minimize", "minus", "minusInCircle", "minusInCircleFilled", "mobile", "monitoringApp", "moon", "nested", "node", "notebookApp", "number", "offline", "online", "outlierDetectionJob", "package", "packetbeatApp", "pageSelect", "pagesSelect", "paperClip", "partial", "pause", "payment", "pencil", "percent", "pin", "pinFilled", "pipelineApp", "play", "playFilled", "plus", "plusInCircle", "plusInCircleFilled", "popout", "push", "questionInCircle", "quote", "recentlyViewedApp", "refresh", "regressionJob", "reporter", "reportingApp", "returnKey", "save", "savedObjectsApp", "scale", "search", "searchProfilerApp", "securityAnalyticsApp", "securityApp", "securitySignal", "securitySignalDetected", "securitySignalResolved", "sessionViewer", "shard", "share", "snowflake", "sortDown", "sortLeft", "sortRight", "sortUp", "sortable", "spacesApp", "sqlApp", "starEmpty", "starEmptySpace", "starFilled", "starFilledSpace", "starMinusEmpty", "starMinusFilled", "starPlusEmpty", "starPlusFilled", "stats", "stop", "stopFilled", "stopSlash", "storage", "string", "submodule", "sun", "swatchInput", "symlink", "tableDensityCompact", "tableDensityExpanded", "tableDensityNormal", "tableOfContents", "tag", "tear", "temperature", "timeline", "timelionApp", "timeRefresh", "timeslider", "training", "trash", "unfold", "unlink", "upgradeAssistantApp", "uptimeApp", "user", "userAvatar", "users", "usersRolesApp", "vector", "videoPlayer", "visArea", "visAreaStacked", "visBarHorizontal", "visBarHorizontalStacked", "visBarVertical", "visBarVerticalStacked", "visGauge", "visGoal", "visLine", "visMapCoordinate", "visMapRegion", "visMetric", "visPie", "visTable", "visTagCloud", "visText", "visTimelion", "visVega", "visVisualBuilder", "visualizeApp", "watchesApp", "wordWrap", "wordWrapDisabled", "workplaceSearchApp", "wrench", "tokenClass", "tokenProperty", "tokenEnum", "tokenVariable", "tokenMethod", "tokenAnnotation", "tokenException", "tokenInterface", "tokenParameter", "tokenField", "tokenElement", "tokenFunction", "tokenBoolean", "tokenString", "tokenArray", "tokenNumber", "tokenConstant", "tokenObject", "tokenEvent", "tokenKey", "tokenNull", "tokenStruct", "tokenPackage", "tokenOperator", "tokenEnumMember", "tokenRepo", "tokenSymbol", "tokenFile", "tokenModule", "tokenNamespace", "tokenDate", "tokenIP", "tokenNested", "tokenAlias", "tokenShape", "tokenGeo", "tokenRange", "tokenBinary", "tokenJoin", "tokenPercolator", "tokenFlattened", "tokenRankFeature", "tokenRankFeatures", "tokenKeyword", "tokenTag", "tokenCompletionSuggester", "tokenDenseVector", "tokenText", "tokenTokenCount", "tokenSearchType", "tokenHistogram"]).isRequired, PropTypes.string.isRequired, PropTypes.elementType.isRequired]),

  /**
     * Additional EuiIcon props to apply to the optional icon
     */
  iconProps: PropTypes.any,

  /**
     * Optional array breadcrumbs that render before the `pageTitle`
     */
  breadcrumbs: PropTypes.arrayOf(PropTypes.shape({
    className: PropTypes.string,
    "aria-label": PropTypes.string,
    "data-test-subj": PropTypes.string,
    href: PropTypes.string,
    onClick: PropTypes.func,

    /**
         * Visible label of the breadcrumb
         */
    text: PropTypes.node.isRequired,

    /**
         * Force a max-width on the breadcrumb text
         */
    truncate: PropTypes.bool,

    /**
         * Accepts any EuiLink `color` when rendered as one (has `href` or `onClick`)
         */
    color: PropTypes.any,

    /**
         * Override the existing `aria-current` which defaults to `page` for the last breadcrumb
         */
    "aria-current": PropTypes.any
  }).isRequired),

  /**
     * Adjust the props of [EuiBreadcrumbs](#/navigation/breadcrumbs)
     */
  breadcrumbProps: PropTypes.any,

  /**
     * In-app navigation presented as large borderless tabs.
     * Accepts an array of `EuiTab` objects;
     */
  tabs: PropTypes.arrayOf(PropTypes.shape({
    /**
       * Visible text of the tab
       */
    label: PropTypes.node.isRequired
  }).isRequired),

  /**
     * Any extras to apply to the outer tabs container.
     * Extends `EuiTabs`
     */
  tabsProps: PropTypes.shape({
    className: PropTypes.string,
    "aria-label": PropTypes.string,
    "data-test-subj": PropTypes.string,

    /**
         * ReactNode to render as this component's content
         */
    children: PropTypes.node,

    /**
         * Adds a bottom border to separate it from the content after
         */
    bottomBorder: PropTypes.bool
  }),

  /**
     * Sets the max-width of the page,
     * set to `true` to use the default size of `1000px (1200 for Amsterdam)`,
     * set to `false` to not restrict the width,
     * set to a number for a custom width in px,
     * set to a string for a custom width in custom measurement.
     */
  restrictWidth: PropTypes.oneOfType([PropTypes.bool.isRequired, PropTypes.number.isRequired, PropTypes.string.isRequired])
};

try {
  EuiPageHeader.__docgenInfo = {
    tags: {},
    filePath: '/app/src/components/page/page_header/page_header.tsx',
    description: '',
    displayName: 'EuiPageHeader',
    methods: [],
    props: {
      paddingSize: {
        defaultValue: {
          value: 'none'
        },
        description: 'Adjust the padding.\n' + "When using this setting it's best to be consistent throughout all similar usages",
        name: 'paddingSize',
        parent: {
          fileName: 'app/src/components/page/page_header/page_header.tsx',
          name: 'EuiPageHeaderProps'
        },
        declarations: [{
          fileName: 'app/src/components/page/page_header/page_header.tsx',
          name: 'EuiPageHeaderProps'
        }],
        required: false,
        type: {
          name: 'enum',
          raw: '"none" | "s" | "m" | "l"',
          value: [{
            value: '"none"'
          }, {
            value: '"s"'
          }, {
            value: '"m"'
          }, {
            value: '"l"'
          }]
        }
      },
      bottomBorder: {
        defaultValue: null,
        description: 'Adds a bottom border to separate it from the content after',
        name: 'bottomBorder',
        parent: {
          fileName: 'app/src/components/page/page_header/page_header.tsx',
          name: 'EuiPageHeaderProps'
        },
        declarations: [{
          fileName: 'app/src/components/page/page_header/page_header.tsx',
          name: 'EuiPageHeaderProps'
        }],
        required: false,
        type: {
          name: 'boolean'
        }
      },
      className: {
        defaultValue: null,
        description: '',
        name: 'className',
        parent: {
          fileName: 'app/src/components/common.ts',
          name: 'CommonProps'
        },
        declarations: [{
          fileName: 'app/src/components/common.ts',
          name: 'CommonProps'
        }],
        required: false,
        type: {
          name: 'string'
        }
      },
      'aria-label': {
        defaultValue: null,
        description: '',
        name: 'aria-label',
        parent: {
          fileName: 'app/src/components/common.ts',
          name: 'CommonProps'
        },
        declarations: [{
          fileName: 'app/src/components/common.ts',
          name: 'CommonProps'
        }],
        required: false,
        type: {
          name: 'string'
        }
      },
      'data-test-subj': {
        defaultValue: null,
        description: '',
        name: 'data-test-subj',
        parent: {
          fileName: 'app/src/components/common.ts',
          name: 'CommonProps'
        },
        declarations: [{
          fileName: 'app/src/components/common.ts',
          name: 'CommonProps'
        }],
        required: false,
        type: {
          name: 'string'
        }
      },
      responsive: {
        defaultValue: {
          value: 'true'
        },
        description: "Set to false if you don't want the children to stack at small screen sizes.\n" + 'Set to `reverse` to display the right side content first for the sake of hierarchy (like global time)',
        name: 'responsive',
        parent: {
          fileName: 'app/src/components/page/page_header/page_header_content.tsx',
          name: '_EuiPageHeaderContentProps'
        },
        declarations: [{
          fileName: 'app/src/components/page/page_header/page_header_content.tsx',
          name: '_EuiPageHeaderContentProps'
        }],
        required: false,
        type: {
          name: 'boolean | "reverse"'
        }
      },
      alignItems: {
        defaultValue: null,
        description: 'Vertical alignment of the left and right side content;\n' + 'Default is `middle` for custom content, but `top` for when `pageTitle` or `tabs` are included',
        name: 'alignItems',
        parent: {
          fileName: 'app/src/components/page/page_header/page_header_content.tsx',
          name: '_EuiPageHeaderContentProps'
        },
        declarations: [{
          fileName: 'app/src/components/page/page_header/page_header_content.tsx',
          name: '_EuiPageHeaderContentProps'
        }],
        required: false,
        type: {
          name: 'enum',
          raw: '"center" | "top" | "bottom" | "stretch"',
          value: [{
            value: '"center"'
          }, {
            value: '"top"'
          }, {
            value: '"bottom"'
          }, {
            value: '"stretch"'
          }]
        }
      },
      rightSideItems: {
        defaultValue: null,
        description: 'Pass custom an array of content to this side usually up to 3 buttons.\n' + 'The first button should be primary, usually with `fill` and will be visually displayed as the last item,\n' + 'but first in the tab order',
        name: 'rightSideItems',
        parent: {
          fileName: 'app/src/components/page/page_header/page_header_content.tsx',
          name: '_EuiPageHeaderContentProps'
        },
        declarations: [{
          fileName: 'app/src/components/page/page_header/page_header_content.tsx',
          name: '_EuiPageHeaderContentProps'
        }],
        required: false,
        type: {
          name: 'ReactNode[]'
        }
      },
      rightSideGroupProps: {
        defaultValue: null,
        description: 'Additional EuiFlexGroup props to pass to the container of the `rightSideItems`',
        name: 'rightSideGroupProps',
        parent: {
          fileName: 'app/src/components/page/page_header/page_header_content.tsx',
          name: '_EuiPageHeaderContentProps'
        },
        declarations: [{
          fileName: 'app/src/components/page/page_header/page_header_content.tsx',
          name: '_EuiPageHeaderContentProps'
        }],
        required: false,
        type: {
          name: 'Partial<EuiFlexGroupProps>'
        }
      },
      description: {
        defaultValue: null,
        description: 'Position is dependent on existing with a `pageTitle` or `tabs`\n' + 'Automatically get wrapped in a single paragraph tag inside an EuiText block',
        name: 'description',
        parent: {
          fileName: 'app/src/components/page/page_header/page_header_content.tsx',
          name: 'EuiPageHeaderContentLeft'
        },
        declarations: [{
          fileName: 'app/src/components/page/page_header/page_header_content.tsx',
          name: 'EuiPageHeaderContentLeft'
        }],
        required: false,
        type: {
          name: 'ReactNode'
        }
      },
      pageTitle: {
        defaultValue: null,
        description: 'Wrapped in an `H1` so choose appropriately.\nA simple string is best',
        name: 'pageTitle',
        parent: {
          fileName: 'app/src/components/page/page_header/page_header_content.tsx',
          name: 'EuiPageHeaderContentTitle'
        },
        declarations: [{
          fileName: 'app/src/components/page/page_header/page_header_content.tsx',
          name: 'EuiPageHeaderContentTitle'
        }],
        required: false,
        type: {
          name: 'ReactNode'
        }
      },
      pageTitleProps: {
        defaultValue: null,
        description: 'Additional props to pass to the EuiTitle',
        name: 'pageTitleProps',
        parent: {
          fileName: 'app/src/components/page/page_header/page_header_content.tsx',
          name: 'EuiPageHeaderContentTitle'
        },
        declarations: [{
          fileName: 'app/src/components/page/page_header/page_header_content.tsx',
          name: 'EuiPageHeaderContentTitle'
        }],
        required: false,
        type: {
          name: 'Omit<EuiTitleProps, "children" | "size">'
        }
      },
      iconType: {
        defaultValue: null,
        description: 'Optional icon to place to the left of the title',
        name: 'iconType',
        parent: {
          fileName: 'app/src/components/page/page_header/page_header_content.tsx',
          name: 'EuiPageHeaderContentTitle'
        },
        declarations: [{
          fileName: 'app/src/components/page/page_header/page_header_content.tsx',
          name: 'EuiPageHeaderContentTitle'
        }],
        required: false,
        type: {
          name: 'IconType'
        }
      },
      iconProps: {
        defaultValue: null,
        description: 'Additional EuiIcon props to apply to the optional icon',
        name: 'iconProps',
        parent: {
          fileName: 'app/src/components/page/page_header/page_header_content.tsx',
          name: 'EuiPageHeaderContentTitle'
        },
        declarations: [{
          fileName: 'app/src/components/page/page_header/page_header_content.tsx',
          name: 'EuiPageHeaderContentTitle'
        }],
        required: false,
        type: {
          name: 'Partial<Omit<EuiIconProps, "type">>'
        }
      },
      breadcrumbs: {
        defaultValue: null,
        description: 'Optional array breadcrumbs that render before the `pageTitle`',
        name: 'breadcrumbs',
        parent: {
          fileName: 'app/src/components/page/page_header/page_header_content.tsx',
          name: 'EuiPageHeaderContentTitle'
        },
        declarations: [{
          fileName: 'app/src/components/page/page_header/page_header_content.tsx',
          name: 'EuiPageHeaderContentTitle'
        }],
        required: false,
        type: {
          name: 'EuiBreadcrumb[]'
        }
      },
      breadcrumbProps: {
        defaultValue: null,
        description: 'Adjust the props of [EuiBreadcrumbs](#/navigation/breadcrumbs)',
        name: 'breadcrumbProps',
        parent: {
          fileName: 'app/src/components/page/page_header/page_header_content.tsx',
          name: 'EuiPageHeaderContentTitle'
        },
        declarations: [{
          fileName: 'app/src/components/page/page_header/page_header_content.tsx',
          name: 'EuiPageHeaderContentTitle'
        }],
        required: false,
        type: {
          name: 'Partial<Omit<EuiBreadcrumbsProps, "breadcrumbs">>'
        }
      },
      tabs: {
        defaultValue: null,
        description: 'In-app navigation presented as large borderless tabs.\n' + 'Accepts an array of `EuiTab` objects;',
        name: 'tabs',
        parent: {
          fileName: 'app/src/components/page/page_header/page_header_content.tsx',
          name: 'EuiPageHeaderContentTabs'
        },
        declarations: [{
          fileName: 'app/src/components/page/page_header/page_header_content.tsx',
          name: 'EuiPageHeaderContentTabs'
        }],
        required: false,
        type: {
          name: 'Tab[]'
        }
      },
      tabsProps: {
        defaultValue: null,
        description: 'Any extras to apply to the outer tabs container.\nExtends `EuiTabs`',
        name: 'tabsProps',
        parent: {
          fileName: 'app/src/components/page/page_header/page_header_content.tsx',
          name: 'EuiPageHeaderContentTabs'
        },
        declarations: [{
          fileName: 'app/src/components/page/page_header/page_header_content.tsx',
          name: 'EuiPageHeaderContentTabs'
        }],
        required: false,
        type: {
          name: 'Omit<EuiTabsProps, "size" | "expand">'
        }
      },
      restrictWidth: {
        defaultValue: {
          value: 'false'
        },
        description: 'Sets the max-width of the page,\n' + 'set to `true` to use the default size of `1000px (1200 for Amsterdam)`,\n' + 'set to `false` to not restrict the width,\n' + 'set to a number for a custom width in px,\n' + 'set to a string for a custom width in custom measurement.',
        name: 'restrictWidth',
        parent: undefined,
        declarations: [{
          fileName: 'app/src/components/page/_restrict_width.ts',
          name: 'TypeLiteral'
        }],
        required: false,
        type: {
          name: 'string | number | boolean'
        }
      }
    },
    extendedInterfaces: ['EuiPageHeaderProps', 'CommonProps', 'HTMLAttributes', 'AriaAttributes', 'DOMAttributes', '_EuiPageHeaderContentProps', 'EuiPageHeaderContentLeft', 'EuiPageHeaderContentTitle', 'EuiPageHeaderContentTabs']
  };
} catch (e) {}